import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, type Recording } from "../utils/supabase";
import { useAuth } from "../contexts/AuthContext";
import { format } from "date-fns";
import { useLoadingState } from "../hooks/useLoadingState";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useToast } from "../contexts/ToastContext";
import { useKeyboardShortcut } from "../hooks/useKeyboardShortcut";
import { AudioControls } from "../components/AudioControls";
import { motion } from "framer-motion";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { PullToRefreshIndicator } from "../components/PullToRefreshIndicator";
import { SkeletonList } from "../components/SkeletonLoader";

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
};

export default function Dashboard() {
    const { user } = useAuth();
    const [recordings, setRecordings] = useState<Recording[]>([]);
    const { 
        isLoading, 
        status,
        message,
        operation,
        progress,
        estimatedTimeRemaining,
        startLoading, 
        stopLoading, 
        setError,
        updateProgress 
    } = useLoadingState();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    
    useKeyboardShortcut({
        n: () => navigate("/"),
        r: () => loadRecordings(),
    });

    // Pull to refresh functionality
    const { isPulling, pullDistance, thresholdExceeded, indicatorRef } = usePullToRefresh({
        targetRef: containerRef,
        onRefresh: async () => {
            setIsRefreshing(true);
            try {
                await loadRecordings();
                setRefreshKey(prev => prev + 1);
                showToast("Recordings refreshed", "success");
            } catch (error) {
                showToast("Failed to refresh recordings", "error");
            } finally {
                setIsRefreshing(false);
            }
        },
        disabled: isLoading || isRefreshing,
        isRefreshing,
    });

    useEffect(() => {
        if (user) {
            loadRecordings();
        }
    }, [user]);

    async function loadRecordings() {
        try {
            startLoading("Preparing to load recordings...", "Loading Recordings");
            
            // Simulate progress updates for better UX
            let progress = 0;
            const progressInterval = setInterval(() => {
                progress += 10;
                if (progress <= 90) {
                    updateProgress(progress, 100);
                }
            }, 200);

            const { data, error } = await supabase
                .from("recordings")
                .select("*")
                .order("created_at", { ascending: false });

            clearInterval(progressInterval);

            if (error) throw error;
            
            updateProgress(100, 100);
            setRecordings(data || []);
            stopLoading("Recordings loaded successfully");
        } catch (error) {
            setError(
                error instanceof Error ? error : new Error("Failed to load recordings"),
                "Loading Recordings"
            );
        }
    }

    async function deleteRecording(id: string) {
        try {
            const recording = recordings.find(rec => rec.id === id);
            if (!recording) throw new Error("Recording not found");

            startLoading(
                `Deleting recording "${recording.name}"...`,
                "Deleting Recording"
            );

            // Simulate progress for better UX
            updateProgress(0, 100);
            setTimeout(() => updateProgress(50, 100), 300);

            const { error } = await supabase
                .from("recordings")
                .delete()
                .eq("id", id);

            if (error) throw error;

            updateProgress(100, 100);
            setRecordings(recordings.filter((rec) => rec.id !== id));
            stopLoading("Recording deleted successfully");
            showToast("Recording deleted successfully", "success");
        } catch (error) {
            setError(
                error instanceof Error ? error : new Error("Failed to delete recording"),
                "Deleting Recording"
            );
            showToast("Failed to delete recording", "error");
        }
    }

    // Enhanced staggered container animation
    const enhancedContainer = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08,
                delayChildren: isRefreshing ? 0.3 : 0,
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };

    // Enhanced item animation
    const enhancedItem = {
        hidden: { opacity: 0, y: 15 },
        show: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: "spring",
                stiffness: 260,
                damping: 20
            } 
        }
    };

    return (
        <div className="relative" ref={containerRef}>
            {/* Pull to refresh indicator */}
            <PullToRefreshIndicator 
                pullDistance={pullDistance}
                thresholdExceeded={thresholdExceeded}
                isRefreshing={isRefreshing}
                indicatorRef={indicatorRef}
            />
            
            {/* Show full loading overlay only when it's not a standard loading operation */}
            {isLoading && operation !== "Loading Recordings" && (
                <LoadingOverlay 
                    message={message}
                    status={status}
                    operation={operation}
                    progress={progress}
                    estimatedTimeRemaining={estimatedTimeRemaining}
                    fullScreen={false}
                />
            )}
            
            <div>
                <div className="flex justify-between items-center mb-6 sm:mb-8 px-1">
                    <h1>Dashboard</h1>
                    <button
                        onClick={() => navigate('/')}
                        className="btn btn-primary py-3 sm:py-2 px-5 sm:px-4 rounded-xl sm:rounded-lg touch-manipulation relative group"
                    >
                        {/* Touch ripple effect */}
                        <span className="absolute inset-0 pointer-events-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-white/10 rounded-xl sm:rounded-lg scale-0 origin-center"></span>
                        
                        <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        New Recording
                    </button>
                </div>
                
                {/* Show refreshing state in addition to loading overlay */}
                {isRefreshing && !isLoading && (
                    <div className="mb-6 sm:mb-4 text-center text-text-secondary text-base sm:text-sm animate-pulse">
                        Refreshing recordings...
                    </div>
                )}

                {/* Show skeleton loaders when loading recordings */}
                {isLoading && operation === "Loading Recordings" ? (
                    <SkeletonList count={3} layout="grid" />
                ) : (
                    <motion.div 
                        key={refreshKey}
                        variants={enhancedContainer}
                        initial="hidden"
                        animate="show"
                        className="grid gap-6 sm:gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                    >
                        {recordings.length === 0 ? (
                            <motion.div variants={enhancedItem} className="col-span-full">
                                <div className="text-center py-12">
                                    <div className="max-w-md mx-auto">
                                        <div className="card card-hover p-6 sm:p-5">
                                            <div className="flex justify-center">
                                                <div className="relative animate-float">
                                                    <svg className="h-16 w-16 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                    </svg>
                                                    <div className="absolute -right-1 -top-1">
                                                        <svg className="h-6 w-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                            <h2 className="mt-6">Create Your First Recording</h2>
                                            <p className="mt-2">
                                                Get started with SpeakRx by creating your first recording. You can:
                                            </p>
                                            <ul className="mt-4 space-y-3">
                                                <li className="flex items-center text-text-secondary">
                                                    <svg className="h-5 w-5 text-primary mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Record directly from your microphone
                                                </li>
                                                <li className="flex items-center text-text-secondary">
                                                    <svg className="h-5 w-5 text-primary mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Upload an existing audio file
                                                </li>
                                                <li className="flex items-center text-text-secondary">
                                                    <svg className="h-5 w-5 text-primary mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Import from a URL
                                                </li>
                                            </ul>
                                            <div className="mt-6">
                                                <button
                                                    onClick={() => navigate('/')}
                                                    className="btn btn-primary w-full py-3 sm:py-2 rounded-xl sm:rounded-lg"
                                                >
                                                    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                    </svg>
                                                    Start New Recording
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ) : (
                            recordings.map((recording, index) => (
                                <motion.div
                                    key={recording.id}
                                    variants={enhancedItem}
                                    className="card card-hover group relative overflow-hidden tap-highlight-none touch-manipulation p-5 sm:p-4"
                                    tabIndex={0}
                                    role="article"
                                    aria-label={`Recording: ${recording.name}`}
                                    style={{
                                        animationDelay: `${index * 50}ms`
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* Touch ripple effect for mobile */}
                                    <span className="absolute inset-0 pointer-events-none touch-none opacity-0 group-active:opacity-100 group-active:animate-touch-ripple bg-primary/10 rounded-xl scale-0 origin-center"></span>
                                    
                                    <div className="flex justify-between items-start mb-5 sm:mb-4">
                                        <h3 className="font-semibold text-lg">{recording.name}</h3>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteRecording(recording.id);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-error hover:text-error/80 transition-all h-12 w-12 sm:h-10 sm:w-10 flex items-center justify-center rounded-full hover:bg-error/10"
                                            aria-label="Delete recording"
                                        >
                                            <svg className="h-6 w-6 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                    
                                    <AudioControls
                                        src={recording.audio_url}
                                        type="audio/wav"
                                    />
                                    
                                    <div className="mt-5 sm:mt-4 text-base sm:text-sm text-text-secondary space-y-2 sm:space-y-1">
                                        <p>Duration: {Math.round(recording.duration)}s</p>
                                        <p>Created: {format(new Date(recording.created_at), "PPp")}</p>
                                    </div>
                                    
                                    {recording.transcript && (
                                        <div className="mt-5 sm:mt-4 p-5 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl text-base sm:text-sm">
                                            {recording.transcript}
                                        </div>
                                    )}
                                </motion.div>
                            ))
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
}
